/**
 * Delegate policies (ADR 0074): a named agent answers a mission's routine approval requests,
 * within a policy a person sets per project. The hub judges each request (delegateVerdict in
 * @orgus/policy) and answers in the agent's name; everything else waits for people as before.
 *
 *   GET    /api/approvals/delegates              policies and the agent tokens that qualify
 *   PUT    /api/approvals/delegates/:projectId   set or change (an admin or owner, a person)
 *   DELETE /api/approvals/delegates/:projectId   revoke (an operator or above, a person)
 */
import type { Id, Ts } from '../index.ts';

/** What a delegate may answer. Each is opt-in per project. */
export type DelegateClass = 'edits' | 'tests' | 'typecheck' | 'checks' | 'git' | 'install' | 'land';

export const DELEGATE_CLASSES: readonly DelegateClass[] = ['edits', 'tests', 'typecheck', 'checks', 'git', 'install', 'land'];

export const DELEGATE_CLASS_LABEL: Record<DelegateClass, string> = {
  edits: 'Edits in the worktree',
  tests: 'Test runs',
  typecheck: 'Type checks',
  checks: 'Repo checks',
  git: 'Local git',
  install: 'Offline install',
  land: 'Landing',
};

export const DELEGATE_CLASS_DETAIL: Record<DelegateClass, string> = {
  edits: 'Edit and Write of files inside the mission worktree.',
  tests: 'pnpm test, test:affected and vitest run.',
  typecheck: 'pnpm typecheck and tsc --noEmit.',
  checks: 'pnpm check, lint, and orgus check, layout and watch.',
  git: 'status, diff, log, show, add, commit and merge main. Never push or fetch.',
  install: 'pnpm install --offline, from the local store only.',
  land: 'orgus land: runs the checks and moves the local main. Never pushes.',
};

/** The scope an agent token needs to be named in a delegate policy. */
export const DELEGATE_SCOPE = 'approval.approve';
export const DELEGATE_PER_MINUTE_DEFAULT = 20;
export const DELEGATE_PER_MINUTE_MAX = 60;
export const DELEGATE_DAYS_DEFAULT = 7;
export const DELEGATE_DAYS_MAX = 30;

export interface DelegatePolicy {
  /** dlg_<ulid>, kept across versions. */
  id: Id;
  projectId: Id;
  /** The agent token's name: answers are given and audited as agent:<agent>. */
  agent: string;
  classes: DelegateClass[];
  /** Answers per minute; past it, requests wait for people. */
  perMinute: number;
  /** Never past the token's own expiry (the hub checks on every answer). */
  expiresAt: Ts;
  /** Every change is a new version; answers name the version they were given under. */
  version: number;
  createdBy: string;
  createdAt: Ts;
  updatedBy: string;
  updatedAt: Ts;
  /** Set when a person revoked it: it answers nothing from then on. */
  revokedBy?: string;
  revokedAt?: Ts;
  /** How many requests it answered (all versions). */
  answered?: number;
  lastAnsweredAt?: Ts;
}

/** Body of PUT /api/approvals/delegates/:projectId. */
export interface DelegatePolicyInput {
  agent: string;
  classes: DelegateClass[];
  /** 1..DELEGATE_PER_MINUTE_MAX; default DELEGATE_PER_MINUTE_DEFAULT. */
  perMinute?: number;
  /** 1..DELEGATE_DAYS_MAX from now; default DELEGATE_DAYS_DEFAULT. */
  days?: number;
}

/** An agent token as the delegate picker sees it. Never the secret. */
export interface DelegateAgent {
  name: string;
  expiresAt: Ts;
  /** Null when it can be named; else why not, in one sentence. */
  problem: string | null;
}

/** GET /api/approvals/delegates */
export interface DelegatesResponse {
  policies: DelegatePolicy[];
  /** Agent tokens on this hub (admins only; empty for others). */
  agents: DelegateAgent[];
}

/** On a request a delegate answered. */
export interface ApprovalDelegation {
  policyId: Id;
  version: number;
  /** agent:<name> */
  agent: string;
  class: DelegateClass;
}

/** A policy answers now: not revoked and not expired. */
export function delegateActive(p: Pick<DelegatePolicy, 'revokedAt' | 'expiresAt'>, now: number): boolean {
  return !p.revokedAt && p.expiresAt > now;
}

/** "agent:x under the delegate policy (v3)": who answered, with the policy named. */
export function delegatedBy(d: Pick<ApprovalDelegation, 'agent' | 'version'>): string {
  return `${d.agent} under the delegate policy (v${d.version})`;
}

/** "Delegate v3": the policy a delegated answer was given under, short enough for a fact. */
export function delegatedPolicyText(d: Pick<ApprovalDelegation, 'version'>): string {
  return `Delegate v${d.version}`;
}

/** "Answered by agent:x under the delegate policy (v3): test runs." */
export function delegatedNote(d: ApprovalDelegation): string {
  return `Answered by ${delegatedBy(d)}: ${DELEGATE_CLASS_LABEL[d.class].toLowerCase()}.`;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A plain-language problem with a policy body, or null. Shared by the hub and the console. */
export function delegateInputError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the delegate policy as a JSON object.';
  if (typeof v.agent !== 'string' || !/^[A-Za-z0-9._-]{1,40}$/.test(v.agent)) return 'Name the agent token that answers: its name, as Settings, API tokens shows it.';
  if (!Array.isArray(v.classes) || !v.classes.length) return 'Pick at least one kind of request the agent may answer.';
  const bad = v.classes.find((c) => !(DELEGATE_CLASSES as readonly unknown[]).includes(c));
  if (bad !== undefined) return `${String(bad)} is not a kind of request a delegate can answer.`;
  if (v.perMinute !== undefined && (typeof v.perMinute !== 'number' || !Number.isInteger(v.perMinute) || v.perMinute < 1 || v.perMinute > DELEGATE_PER_MINUTE_MAX)) {
    return `Answers per minute must be a whole number from 1 to ${DELEGATE_PER_MINUTE_MAX}.`;
  }
  if (v.days !== undefined && (typeof v.days !== 'number' || !Number.isInteger(v.days) || v.days < 1 || v.days > DELEGATE_DAYS_MAX)) {
    return `A delegate policy lasts 1 to ${DELEGATE_DAYS_MAX} days.`;
  }
  const extra = Object.keys(v).find((k) => !['agent', 'classes', 'perMinute', 'days'].includes(k));
  return extra ? `${extra} is not part of a delegate policy.` : null;
}

export const delegatePolicyPath = (projectId: string): string => `/api/approvals/delegates/${encodeURIComponent(projectId)}`;
export const DELEGATES_PATH = '/api/approvals/delegates';
